<template>
    <div class="course-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && course.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            :key="courseKey"
                            v-model="course"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                v-if="editMode"
                                class="btn btn-success waves-effect waves-light m-1"
                                @click="onChangeRegistration"
                            >
                                <i class="far fa-calendar" />
                                Registration range
                            </button>

                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>

                            <template
                                v-if="editMode && course.status === 'draft'"
                            >
                                <button
                                    class="btn btn-success btn-static waves-effect waves-light m-1"
                                    @click="publish"
                                >
                                    <i class="fa fa-check-circle" />
                                    Publish
                                </button>

                                <button
                                    class="btn btn-success btn-static waves-effect waves-light m-1"
                                    @click="preview"
                                >
                                    <i class="far fa-eye"></i>
                                    Preview
                                </button>
                            </template>

                            <router-link
                                v-if="editMode"
                                :to="`/courses/${course.id}/users`"
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                            >
                                <i class="fas fa-users" />
                                Users
                            </router-link>

                            <button
                                v-if="editMode"
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="duplicate"
                            >
                                <i class="far fa-copy" />
                                Duplicate
                            </button>

                            <div v-if="editMode" class="card section mt-3">
                                <div class="card-body">
                                    <div class="row">
                                        <div
                                            class="d-flex flex-grow-1 justify-content-center align-items-center"
                                        >
                                            <div class="text-uppercase h5 mr-2">
                                                Link
                                            </div>
                                            <div
                                                class="btn btn-success"
                                                @click="copyToClipboard"
                                            >
                                                <i class="far fa-copy" />
                                            </div>
                                        </div>
                                        <div class="text-break">
                                            {{ link }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :options="options"
                            :item="course"
                            :campuses="course.campuses"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>

            <change-registration-range-modal
                :value="course"
                @change="onChange"
            />
        </div>

        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import {
    required,
    minLength,
    numeric,
    integer
} from 'vuelidate/lib/validators';
import { frontendUrl } from '@/config';
import { PaymentMethod } from '@/enums/paymentMethods';
import AddEditForm from '@/components/courses/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import ChangeRegistrationRangeModal from '@/components/courses/ChangeRegistrationRangeModal';

export default {
    page: {
        title: 'Manage Course'
    },

    validations: {
        course: {
            teacherId: {
                required
            },
            name: {
                required
            },
            description: {
                required
            },
            price: {
                required,
                numeric
            },
            agudaPrice: {
                numeric
            },
            terminal: {
                required
            },
            limit: {
                required,
                integer
            },
            paymentMethods: {
                required,
                minLength: minLength(1)
            },
            registrationStartsAt: {
                required
            },
            registrationEndsAt: {
                required,
                notBeforeStartDate(v, { registrationStartsAt }) {
                    return this.$dayjs(v).isAfter(registrationStartsAt);
                }
            },
            openingDate: {
                required
            },
            lessons: {
                $each: {
                    startsAt: {
                        required
                    },
                    endsAt: {
                        required,
                        notBeforeStartDate(v, { startsAt }) {
                            return this.$dayjs(v).isAfter(startsAt);
                        }
                    },
                    location: {
                        required
                    }
                }
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation,
        ChangeRegistrationRangeModal
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            id,
            title: id ? 'Edit marathon' : 'Add marathon',
            link: '',
            editMode: !!id,
            course: {
                price: 0,
                agudaPrice: null,
                lessons: [],
                campuses: [],
                terminal: PaymentMethod.IFRAME
            },
            courseKey: 0,
            serverErrors: [],
            options: {}
        };
    },

    computed: {
        items() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Marathons',
                    to: '/courses'
                },
                {
                    text: this.id
                        ? `${this.course.name} (${this.course.number})`
                        : 'Add',
                    active: true
                }
            ];
        },

        sections() {
            const sections = {
                campuses: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.course = await this.getCourse(this.id);

                this.link = `${frontendUrl}/marathons/${this.course.id}`;
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }

            return;
        }

        try {
            const { rows } = await this.getCampuses({ perPage: 5 });

            this.course.campuses = rows;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            getCourse: 'courses/show',
            addCourse: 'courses/store',
            updateCourse: 'courses/update',
            deleteCourse: 'courses/destroy',
            publishCourse: 'courses/publish',
            getCampuses: 'campuses/index'
        }),

        async preview() {
            await this.submitHandler();

            window.open(`${frontendUrl}/marathons/${this.course.id}`, '_blank');
        },

        async publish() {
            try {
                const course = await this.publishCourse(this.course.id);

                this.course = course;

                this.courseKey++;

                this.$toaster('Course has been published!');
            } catch (error) {
                console.error(error);
            }
        },

        async duplicate() {
            const campuses = this.course.campuses.map(campus => campus.id);
            const openingDate = this.$dayjs(this.course.openingDate);
            const lessons = this.course.lessons.map(
                ({ startsAt, endsAt, location }) => {
                    return {
                        location,
                        startsAt: this.$dayjs(startsAt),
                        endsAt: this.$dayjs(endsAt)
                    };
                }
            );

            const course = { ...this.course, campuses, lessons, openingDate };

            delete course.id;
            delete course.createdAt;
            delete course.updatedAt;

            try {
                const newCourse = await this.addCourse(course);

                this.$router.push(`/courses/${newCourse.id}/edit`);
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if (!this.course.agudaPrice) {
                this.course.agudaPrice = null;
            }

            const lessons = this.course.lessons.map(lesson => {
                return {
                    ...lesson,
                    startsAt: this.$dayjs(lesson.startsAt),
                    endsAt: this.$dayjs(lesson.endsAt)
                };
            });

            const openingDate = this.$dayjs(this.course.openingDate);
            const campuses = this.course.campuses.map(campus => campus.id);

            const data = {
                ...this.course,
                openingDate,
                lessons,
                campuses
            };

            try {
                if (!this.editMode) {
                    const added = await this.addCourse(data);

                    this.$router.push(`/courses/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateCourse({
                        id: this.id,
                        data
                    });

                    this.course.updatedAt = updatedAt;

                    this.$toaster('Course updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteCourse(this.id);

                this.$router.push('/courses');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onCampusesChange(campuses) {
            this.course.campuses = campuses;
            this.informationKey++;
        },

        onChangeRegistration() {
            this.$bvModal.show('change-registration-range-modal');
        },

        onChange(data) {
            this.course = { ...this.course, ...data };

            this.courseKey++;
        },

        copyToClipboard() {
            const el = document.createElement('textarea');
            el.value = this.link;

            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');

            document.body.removeChild(el);

            this.$toaster('Copied to clipboard!', {
                title: 'Copied!'
            });
        }
    }
};
</script>
